import React, { useEffect, useRef, useState } from 'react';
import { useCreateFund, useDeleteFund, useUpdateFund } from '../../../../../services/funds.service';
import { useHotkeys } from 'react-hotkeys-hook';
import { UploadedFundFiles } from '../UploadedFundFiles';
import toast from 'react-hot-toast';
import { Box, Button, CloseButton, Flex, Input, Text, Tooltip } from '@mantine/core';
import { TbTrash, TbUpload } from 'react-icons/tb';
import { NewDeleteConfirmModal } from '../../../../../components/NewDeleteConfirmModal';
import { DuplicateFilesModal } from '../../../../../components/DuplicateFilesModal';
import { detectDuplicateFiles } from '../../../../../utils/fileDeduplication';
import { REPORT_NAMINGS } from '../../../../../consts/reportTypes';
import { PermissionGuard } from '../../../../../components/PermissionGuard';
import { DropZone } from '../../../../../components/DropZone';
import { getWorkflowDropzoneAccept, getWorkflowFileAccept } from '../../../../../consts/fileTypes';
import { extractApiError } from '../../../../../utils';
import AddFilesSourceModal from '../../../components/AddFilesSourceModal';

export const FundRow = ({
  onDelete,
  defaultValue,
  fundId,
  refetch,
  onUploadFundFiles,
  individualFiles,
  bulkFiles,
  periodId,
  isDummy,
  hideInput,
  clearUnsavedFunds,
  reportType,
  hideBadge,
  isSelected,
  handleSelect,
  onSelectProtectedFile,
  disableActions,
  canManageWorkflow = true,
  isCloudIntegrationEnabled = false,
  onImportFromIntegration
}) => {
  const ref = useRef();
  const uploadRef = useRef();
  const pendingUploadFilesRef = useRef(null);
  const updateFund = useUpdateFund();
  const deleteFund = useDeleteFund();
  const createFund = useCreateFund();
  const acceptedFileTypes = getWorkflowFileAccept(reportType);
  const acceptedDropzoneTypes = getWorkflowDropzoneAccept(reportType);

  const [value, setValue] = useState(defaultValue);
  const [isEdit, setIsEdit] = useState(!fundId);
  const [showUploadedFiles, setShowUploadedFiles] = useState(false);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isOpenPopover, setIsOpenPopover] = useState(false);
  const [isAddFilesModalOpen, setIsAddFilesModalOpen] = useState(false);
  const [duplicateFiles, setDuplicateFiles] = useState([]);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  const hideActions = isDummy;

  const onClickRename = () => {
    if (disableActions || !canManageWorkflow) return;

    setIsEdit(prev => !prev);
    setIsOpenPopover(false);

    if (!isEdit) ref.current?.focus();
  };

  const onEdit = () => {
    if (disableActions || !canManageWorkflow) return;

    if (value !== defaultValue && fundId) {
      updateFund.mutate(
        {
          fundId,
          data: {
            name: value
          }
        },
        {
          onSuccess: () => {
            refetch();
            setIsOpenPopover(false);
            setIsEdit(false);
            toast.success('Fund name updated successfully!');
          },
          onError: err => {
            toast.error(extractApiError(err, 'Unable to update this fund name.'));
            setValue(defaultValue);
          },
          onSettled: () => {
            setIsOpenPopover(false);
          }
        }
      );
    } else if (value !== defaultValue) {
      createFund.mutate(
        {
          periodId,
          data: {
            name: value
          }
        },
        {
          onSuccess: () => {
            refetch();
            setIsOpenPopover(false);
            setIsEdit(false);
            toast.success('Fund added successfully!');
          },
          onError: err => {
            toast.error(extractApiError(err, 'Unable to add this fund.'));
          },
          onSettled: () => {
            setIsOpenPopover(false);
          }
        }
      );
    }
  };

  const onBlur = () => {
    if (disableActions || !canManageWorkflow) {
      setIsEdit(false);
      return;
    }

    onEdit();
    setIsEdit(false);
  };

  const handleEdit = e => {
    e?.preventDefault();

    if (disableActions || !canManageWorkflow) {
      setIsEdit(false);
      return;
    }

    if (!value) {
      setIsEdit(false);
      clearUnsavedFunds();
      return;
    }

    if (fundId)
      if (isEdit && value !== defaultValue) {
        onEdit();
      } else {
        setIsEdit(false);
      }
    else {
      createFund.mutate(
        {
          periodId,
          data: {
            name: value
          }
        },
        {
          onSuccess: () => {
            refetch();
            setIsEdit(false);
          },

          onError: err => {
            toast.error(extractApiError(err, 'Unable to add this fund.'));
            clearUnsavedFunds();
          }
        }
      );
    }
  };

  useEffect(() => {
    if (individualFiles?.length > 0 || bulkFiles?.length > 0) setShowUploadedFiles(true);
  }, [individualFiles, bulkFiles]);

  useHotkeys(
    'enter',
    e => {
      e.preventDefault();

      if (disableActions || !canManageWorkflow) return;

      if (isEdit) return handleEdit();

      setIsEdit(false);
    },
    [updateFund.isLoading, isEdit]
  );

  const handleDelete = successFn => {
    if (disableActions || !canManageWorkflow) return;

    deleteFund.mutate(fundId, {
      onSuccess: () => {
        refetch();
        setIsOpenDelete(false);
        successFn();
      },
      onError: err => {
        toast.error(extractApiError(err, 'Unable to delete this fund.'));
      }
    });
  };

  const onCloseDelete = () => {
    setIsOpenDelete(false);
  };

  const proceedWithUpload = files => {
    onUploadFundFiles(files, fundId);
    if (uploadRef.current) uploadRef.current.value = '';
  };

  const handleUploadClick = async files => {
    if (disableActions || !canManageWorkflow) return;
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);

    const existingFiles = [...(individualFiles || []), ...(bulkFiles || [])].map(file => ({
      name: file.name,
      size: file.size,
      hash: file.hash,
      chunkHash: file.chunkHash
    }));

    if (existingFiles.length > 0) {
      setIsCheckingDuplicates(true);
      try {
        const duplicates = await detectDuplicateFiles(filesArray, existingFiles);

        if (duplicates.length > 0) {
          pendingUploadFilesRef.current = filesArray;
          setDuplicateFiles(duplicates);
          setIsCheckingDuplicates(false);
          return;
        }
      } catch (err) {
        console.error('Error checking duplicates:', err);
        toast.error('Error checking for duplicate files. Proceeding with upload.');
      }
      setIsCheckingDuplicates(false);
    }

    proceedWithUpload(filesArray);
  };

  return (
    <>
      <Box p="16px">
        {!hideActions && !hideInput && (
          <Flex align="center" justify="space-between" mb="16px">
            {isEdit ? (
              <form onSubmit={handleEdit}>
                <Input
                  disabled={updateFund.isLoading || disableActions}
                  ref={ref}
                  value={value}
                  onBlur={onBlur}
                  autoFocus
                  placeholder="Enter fund"
                  onChange={e => setValue(e.target.value)}
                  miw="300px"
                  maw="642px"
                />
                <button type="submit" style={{ display: 'none' }}></button>
              </form>
            ) : (
              <Box
                sx={() => ({
                  border: '1px solid #CED4DA',
                  borderRadius: '4px'
                })}
                h="36px"
                miw="300px"
                maw="642px"
                onClick={onClickRename}
              >
                <Tooltip
                  maw="300"
                  multiline
                  disabled={defaultValue?.length < 86}
                  label={defaultValue}
                >
                  <Text
                    lh="18px"
                    fz="14px"
                    p="8px 12px"
                    truncate="end"
                    c={defaultValue ? 'black' : 'gray.6'}
                  >
                    {defaultValue || 'Enter Fund Name'}
                  </Text>
                </Tooltip>
              </Box>
            )}
            {!disableActions && defaultValue && (
              <Flex align="center" gap="16px">
                <PermissionGuard allowed={canManageWorkflow}>
                  <DropZone
                    onDrop={files => handleUploadClick(files)}
                    variant="compact"
                    disabled={disableActions || !defaultValue}
                    accept={acceptedDropzoneTypes}
                    disableClick={isCloudIntegrationEnabled}
                  >
                    <Button
                      size="sm"
                      leftSection={<TbUpload />}
                      variant="subtle"
                      onClick={
                        isCloudIntegrationEnabled ? () => setIsAddFilesModalOpen(true) : undefined
                      }
                    >
                      Upload Files
                    </Button>
                  </DropZone>
                </PermissionGuard>

                <input
                  type="file"
                  multiple
                  ref={uploadRef}
                  id={'fund_file_upload_' + fundId}
                  style={{ display: 'none' }}
                  accept={acceptedFileTypes}
                  onChange={({ target: { files } }) => handleUploadClick(files)}
                />

                <PermissionGuard allowed={canManageWorkflow}>
                  <CloseButton
                    onClick={() => setIsOpenDelete(true)}
                    icon={<TbTrash color="#CED4DA" fontSize="16px" />}
                  />
                </PermissionGuard>
              </Flex>
            )}
          </Flex>
        )}

        {individualFiles?.length === 0 && bulkFiles?.length === 0 && (
          <Text c="gray.5" fz="14px" fw={400}>
            No files have been added for this fund yet
          </Text>
        )}

        {showUploadedFiles &&
          !hideActions &&
          (individualFiles?.length > 0 || bulkFiles?.length > 0) && (
            <Flex direction="column" gap="12px">
              {individualFiles?.length > 0 && (
                <UploadedFundFiles
                  title={`Uploaded files (${individualFiles.length})`}
                  files={individualFiles}
                  acceptedFileTypes={acceptedFileTypes}
                  hideBadge={hideBadge}
                  onDelete={onDelete}
                  hideActions={disableActions || !canManageWorkflow}
                  onSelectProtectedFile={onSelectProtectedFile}
                  refetch={refetch}
                />
              )}

              {bulkFiles?.length > 0 && (
                <UploadedFundFiles
                  title={`Bulk uploaded files (${bulkFiles.length})`}
                  files={bulkFiles}
                  acceptedFileTypes={acceptedFileTypes}
                  isLight={true}
                  hideBadge={hideBadge}
                  hideActions={disableActions || !canManageWorkflow}
                  onSelectProtectedFile={onSelectProtectedFile}
                  onDelete={onDelete}
                  refetch={refetch}
                  isBulkUpload
                />
              )}
            </Flex>
          )}
      </Box>
      <NewDeleteConfirmModal
        title={`Delete ${REPORT_NAMINGS.FUND?.[reportType]}`}
        description={`Are you sure you want to permanently delete ${defaultValue}?`}
        isOpen={isOpenDelete}
        onClose={onCloseDelete}
        submitLabel={`Yes, Delete ${REPORT_NAMINGS.FUND?.[reportType]}`}
        cancelLabel="Cancel"
        type="delete"
        onConfirm={handleDelete}
        isLoading={deleteFund.isLoading}
      />

      <AddFilesSourceModal
        opened={isAddFilesModalOpen}
        onClose={() => setIsAddFilesModalOpen(false)}
        onUploadFromDevice={() => {
          setIsAddFilesModalOpen(false);
          uploadRef.current?.click();
        }}
        onImportFromSource={() => {
          setIsAddFilesModalOpen(false);
          onImportFromIntegration?.();
        }}
        isCloudIntegrationEnabled={isCloudIntegrationEnabled}
      />

      <DuplicateFilesModal
        isOpen={duplicateFiles.length > 0}
        duplicates={duplicateFiles}
        onClose={() => {
          setDuplicateFiles([]);
          pendingUploadFilesRef.current = null;
          if (uploadRef.current) uploadRef.current.value = '';
        }}
        onProceed={selectedFiles => {
          const duplicateNames = new Set(duplicateFiles.map(d => `${d.fileName}::${d.fileSize}`));
          const nonDuplicates = (pendingUploadFilesRef.current || []).filter(
            file => !duplicateNames.has(`${file.name}::${file.size}`)
          );
          const filesToUpload = [...nonDuplicates, ...selectedFiles];
          if (filesToUpload.length > 0) {
            proceedWithUpload(filesToUpload);
          }
          setDuplicateFiles([]);
          pendingUploadFilesRef.current = null;
        }}
        isLoading={isCheckingDuplicates}
      />
    </>
  );
};
